import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';

import Modal from 'components/Modal';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import { fetchAssessmentSummary } from 'Workspace/Publications/components/PublicationListItemActions/utils/fetchAssessmentSummary';

import messages from './messages';
import { MODAL_MAX_WIDTH } from './constants';
import { calculateTotalItems } from './helpers';
import {
  LearningContextSection,
  SummaryStats,
  AssessmentRow,
  LoadingSkeleton,
} from './components';
import {
  AssessmentModalContent,
  Subtitle,
  AssessmentList,
} from './styles';
import { svgIconType } from 'Workspace/Publications/utils/constants';

const AssessmentDetails = ({ visible, onDismiss, publicationItem }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    if (!visible || !publicationItem) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setData(null);
    setExpandedIds(new Set());

    fetchAssessmentSummary(publicationItem).then((response) => {
      if (cancelled) return;
      setData(response);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [visible, publicationItem]);

  const { learningContext = {}, assessmentList = [], assessmentItems = {}, isScored } = data || {};
  const totalItems = useMemo(() => calculateTotalItems(assessmentItems), [assessmentItems]);

  const toggleExpanded = (assessmentId) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(assessmentId)) {
        next.delete(assessmentId);
      } else {
        next.add(assessmentId);
      }
      return next;
    });
  };

  const publicationTitle = publicationItem?.title;
  const isReady = !isLoading && !!data;

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      icon={svgIconType.CLIPBOARD_LIST}
      title={intl.formatMessage(messages.modalTitle)}
      maxWidth={MODAL_MAX_WIDTH}
    >
      <AssessmentModalContent>
        {publicationTitle && (
          <Subtitle>
            {intl.formatMessage(messages.summaryFor, { title: publicationTitle })}
          </Subtitle>
        )}

        {!isReady ? (
          <LoadingSkeleton />
        ) : (
          <>
            <LearningContextSection learningContext={learningContext} />
            <SummaryStats
              assessmentCount={assessmentList.length}
              totalItems={totalItems}
              isScored={isScored}
            />
            <AssessmentList>
              {assessmentList.map((assessment) => (
                <AssessmentRow
                  key={assessment.assessmentId}
                  assessment={assessment}
                  items={assessmentItems[assessment.assessmentId] || []}
                  isExpanded={expandedIds.has(assessment.assessmentId)}
                  onToggle={toggleExpanded}
                />
              ))}
            </AssessmentList>
          </>
        )}
      </AssessmentModalContent>
    </Modal>
  );
};

AssessmentDetails.propTypes = {
  visible: PropTypes.bool,
  onDismiss: PropTypes.func.isRequired,
  publicationItem: PropTypes.shape({
    activityId: PropTypes.string,
    outputType: PropTypes.string,
    market: PropTypes.string,
    title: PropTypes.string,
  }),
};

export default withTheme(AssessmentDetails);
